import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { connect, useSelector } from 'react-redux';
import {
  detailedSection,
  generalSection,
  initialState,
  initialValues,
  validationSchema,
} from './options';
import BaseForm from 'containers/Base/BaseForm.js';
import { getGeneralInfo, getUserAssignedDepartmentList } from 'reducers/users';
import { transformUserObject } from 'utils/DataTransform/utils/user';
import { isBase64 } from 'utils/checkImage';
import staticText from 'static/text_display.json';
import { ROLES } from 'utils/constants';
import { CustomerInfo } from 'utils/constants/informationText';
import { HelpLinks } from 'utils/constants/helpLinks';
import isEmpty from 'lodash/isEmpty';
import { translatedKeyValueText } from 'utils/methods/helpers';
//actions
import { actions as customerActions } from '../Modules/dataGridTable';
import { showSnackbar } from 'reducers/ui';
import { getGlobalDepartments } from 'utils/personnelHelper';
import { getGlobalDepartmentRecords } from 'selectors/globalSelectors';
import { getCustomerDetailsTransformed } from '../Selectors/customerSelector';

const filteredDepartments = (vals, allDepartments = []) => {
  if (isEmpty(vals)) {
    return [];
  } else if (
    isEmpty(vals.businessClient) ||
    !vals.businessClient.assignedDepartmentList
  ) {
    return allDepartments;
  }

  const bizDepartmentIds = vals.businessClient.assignedDepartmentList;
  return allDepartments.filter(f => bizDepartmentIds.includes(f.id));
};

export const showBizClientBasedOnDepartments = (
  bizClients,
  selectedDept,
  assignedDeptListforBizClient,
) => {
  // should show bizClients that  belong to selected departments plus that belong to all departments
  if (isEmpty(selectedDept) && isEmpty(assignedDeptListforBizClient)) {
    // if the user has not selected any options then show all bizClients
    return bizClients;
  } else if (isEmpty(selectedDept)) {
    // if the user cleared department options, all bizClients should be visible
    return bizClients;
  } else {
    // if the user has selected departments, then filter bizClient options accordingly
    return bizClients.filter(client => {
      const selectedDeptIds = selectedDept?.map(m => m?.id);
      const assignedDeptList = client.assignedDepartmentList;
      if (assignedDeptList.length === 0) {
        return true; // doesn't have the department assigned and belongs to every department
      } else {
        return assignedDeptList.some(
          // all the biz clients which has the assigned selected department
          s => selectedDeptIds.includes(s.id),
        );
      }
    });
  }
};
export const departmentExistInBizClient = vals => {
  const selectedDeptIds = vals.departments?.map(m => m?.id);
  const assignedDeptList = vals.businessClient.assignedDepartmentList || [];
  // if no department is selected or bizclient has no assigned department then show all options to select any bizclient
  if (assignedDeptList.length === 0 || selectedDeptIds.length === 0) {
    return true;
  }
  const y = assignedDeptList?.some(s => selectedDeptIds.includes(s));
  return y;
};

const CustomerForm = ({
  client,
  updateClient,
  createClient,
  editMode,
  closeForm,
  generalInfo,
  countries,
  languages,
  // from customer embedded form
  embeddedselectedBusinessClient,
  embeddedForm,
  disablePresist,
  persistentStateName,
}) => {
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [formVals, setFormVals] = useState({});
  // const businessClients = useBusinessClients();
  const userAssignedDepartments = useSelector(state =>
    getUserAssignedDepartmentList(state),
  );
  const onChangeBizValues = values => {
    if (values?.departments) {
      setSelectedDepartments(values?.departments);
      // check if the selected department exists in the selected business client
      // if yes, then don't change anything
      // if no, then remove the selected option from the businessClient select field
      if (
        !isEmpty(values.businessClient) &&
        !departmentExistInBizClient(values)
      ) {
        values.businessClient = '';
      }
    }
    if (values) {
      setFormVals(values);
    }
  };

  const onCreate = values => {
    const customerData = transformUserObject({
      ...values,
      reliability: 5,
      role: ROLES.CLIENT.id,
      regionId: generalInfo.regionId,
      securityCompanyId: generalInfo.securityCompanyId,
      securityCompanyName: generalInfo.securityCompanyName,
    });
    createClient(customerData, embeddedForm);
    // incase of embedded, close the form
    if (embeddedForm) closeForm();
  };

  const onEdit = values => {
    const body = {
      ...values,
      reliability: 5,
      role: ROLES.CLIENT.id,
      regionId: generalInfo.regionId,
      securityCompanyId: generalInfo.securityCompanyId,
      securityCompanyName: generalInfo.securityCompanyName,
    };

    if (!isBase64(values.image)) {
      Reflect.deleteProperty(body, 'image');
    }
    const customerData = transformUserObject(body);
    const id = get(client, 'others.id.value');
    updateClient(id, customerData);
  };

  const generalSectionOptions = generalSection(
    countries,
    editMode,
    filteredDepartments(formVals, userAssignedDepartments),
  );

  const detailedSectionOptions = detailedSection(
    // filteredBusinessClients,
    languages,
    embeddedselectedBusinessClient,
    generalInfo.securityCompanyId,
    selectedDepartments,
  );

  return (
    <BaseForm
      edit={editMode}
      onChange={onChangeBizValues}
      initialValues={
        editMode
          ? initialValues(client)
          : {
              ...initialState,
              ...(embeddedselectedBusinessClient && {
                businessClient: embeddedselectedBusinessClient,
              }),
            }
      }
      formTitle={
        editMode
          ? staticText.managerial_view.customer.edit_default
          : staticText.managerial_view.customer.create
      }
      saveHoverText={
        editMode
          ? translatedKeyValueText('update_value_title', {
              value: client.generalInfo.customerId.value,
            })
          : staticText.managerial_view.customer.create
      }
      isImageMandatory={false}
      closeForm={closeForm}
      onSubmit={editMode ? onEdit : onCreate}
      validationSchema={validationSchema}
      generalSection={generalSectionOptions}
      detailedSection={detailedSectionOptions}
      hoverText={editMode ? client.generalInfo.customerId.value : ''}
      note={CustomerInfo.note}
      formInfo={{
        link: HelpLinks.customerLink,
        value: HelpLinks.customerText,
      }}
      disablePresist={disablePresist}
      persistentStateName={persistentStateName}
    />
  );
};

CustomerForm.propTypes = {
  client: PropTypes.object,
  updateClient: PropTypes.func.isRequired,
  createClient: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  countries: PropTypes.array,
  languages: PropTypes.array,
  userAssignedDepartments: PropTypes.array,
  closeForm: PropTypes.func.isRequired,
  generalInfo: PropTypes.object.isRequired,
  handleChange: PropTypes.func,
  setFieldTouched: PropTypes.func,
  setFieldValue: PropTypes.func,
  values: PropTypes.object,
  touched: PropTypes.object,
  errors: PropTypes.object,
  embeddedselectedBusinessClient: PropTypes.object,
  embeddedForm: PropTypes.bool,
  disablePresist: PropTypes.bool,
  persistentStateName: PropTypes.string,
};

const mapStateToProps = state => ({
  client: getCustomerDetailsTransformed(state),
  generalInfo: getGeneralInfo(state),
  countries: state.utils.countries,
  globalDepartmentList: getGlobalDepartments(getGlobalDepartmentRecords(state)),
  languages: state.utils.languages,
});

const mapDispatchToProps = dispatch => ({
  createClient: (customerData, embeddedForm) =>
    dispatch(customerActions.createRowEntry({ customerData, embeddedForm })),
  updateClient: (id, customerData) =>
    dispatch(customerActions.updateRowEntry({ id, customerData })),
  showSnackBar: ({ typeOfSnackbar, message }) =>
    dispatch(showSnackbar({ typeOfSnackbar, message })),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerForm);
